import { useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { ICreateAbsenceRequestDTO, IScheduleStatusDTO } from 'src/api/dtos';
import ScheduleRepository from 'src/api/repositories/ess/v1/schedule.repository';
import { showConfirmationToast } from 'src/shared/components';
import { usePrevious } from 'src/shared/hooks';
import { TranslationFunction, useTranslate } from 'src/shared/providers';
import { getSchedulesStatusOutOfRange, getScheduleStatusDescription } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';

let showWarningPopup = false;

interface IAbsenceRequestValidationProps {
  inputs: Pick<Partial<ICreateAbsenceRequestDTO>, 'isPartialDay' | 'startDate' | 'startTime' | 'endTime'>;
}

export const useScheduleStatusValidation = ({ inputs }: IAbsenceRequestValidationProps) => {
  const { t } = useTranslate();
  const timeFormat = useSelector(getTimeFormat);
  const previousInputs = usePrevious(inputs);

  const [warnings, setWarnings] = useState<IScheduleStatusDTO[]>([]);
  const [inputTargetKey, setInputTargetKey] = useState<keyof ICreateAbsenceRequestDTO | undefined>();

  const resetWarning = () => {
    setWarnings([]);
  };

  const validateStatus = useCallback(async () => {
    if (!inputs.isPartialDay || isEqual(previousInputs, inputs)) {
      return;
    }

    try {
      const { startDate, startTime, endTime } = inputs;

      setWarnings([]);
      setInputTargetKey(undefined);

      if (!startDate || showWarningPopup) {
        return;
      }

      showWarningPopup = true;
      const repository = new ScheduleRepository();
      const response = await repository.getStatuses({ date: startDate });

      if (!response.length) {
        return;
      }

      const outOfRangeStatuses = getSchedulesStatusOutOfRange(response, startDate, startTime, endTime);

      if (outOfRangeStatuses.length >= response.length) {
        showPartialWarningToast({
          statuses: outOfRangeStatuses,
          onCancel: () => {
            showWarningPopup = false;

            const targetKey = getTargetKey(inputs, previousInputs);
            setInputTargetKey(targetKey);
          },
          onSubmit: () => {
            showWarningPopup = false;
          },
          t,
          timeFormat,
        });
      } else {
        showWarningPopup = false;
      }

      setWarnings(response);
    } catch (e) {
      console.error(e);
    }
  }, [inputs]);

  return {
    validateStatus,
    warnings,
    inputTargetKey,
    resetWarning,
  };
};

/**
 *  This method allows setting which date input was last modified and needs to be open
 *  if the user requests to update the startDate, startTime or endTime
 **/
const getTargetKey = (
  inputs: Partial<ICreateAbsenceRequestDTO>,
  previousInputs: Partial<ICreateAbsenceRequestDTO> | undefined,
) => {
  const defaultKey: keyof ICreateAbsenceRequestDTO = 'startDate';

  if (!previousInputs) {
    return defaultKey;
  }

  return Object.entries(inputs).reduce<keyof ICreateAbsenceRequestDTO>((targetKey, [key, value]) => {
    const cKey = key as keyof ICreateAbsenceRequestDTO;

    const previousValue = previousInputs[cKey];

    if (isEqual(previousValue, value)) {
      return targetKey;
    }

    return cKey;
  }, defaultKey);
};

const showPartialWarningToast = ({
  statuses,
  onCancel,
  onSubmit,
  t,
  timeFormat,
}: {
  statuses: IScheduleStatusDTO[];
  onCancel: () => void;
  onSubmit: () => void;
  t: TranslationFunction;
  timeFormat: string | undefined;
}) => {
  const header = t('absences.outsideOfSchedule');
  const message = t('absences.yourAbsenceRequestDoesNotFallWithinYourSchedule');
  const description = statuses.map((status) => getScheduleStatusDescription(status, timeFormat));
  const title = `${header} ${description.join(', ')}`;

  showConfirmationToast({
    type: toast.TYPE.WARNING,
    title,
    cancelText: t('global.no'),
    submitText: t('global.yes'),
    messages: [message],
    onCancel,
    onSubmit,
  });
};
